<template>
  <div>
    <div :style="{ width: width + 'px', height: height + 'px' }" :id="uuid"></div>
    <server-tips id="server-tips" style="width: 400px;" />
  </div>
</template>

<script setup>
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass";
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { createFloor } from "./model-room";
import { createCabinet } from "./model-cabinet";
import { orbitCtrl, trackballCtrl } from "./scene-controls";
import { ambientLight, dirLight } from "./scene-light";
import { createServer } from "./model-server";
// import { TWEEN } from 'three/examples/jsm/libs/tween.module.min.js';
import { createTips } from "./model-server-tips";
import { createLabelRenderer, createRenderer } from "./scene-renderer";
import { onBeforeUnmount, onMounted, watch } from "vue";
import ServerTips from "@/components/three/server-room/server-tips.vue";

const props = defineProps({
  width: Number,
  height: Number,
  option: Object,
  design: Boolean
})

let uuid = require('uuid').v1();
let renderer = null;
let labelRenderer = null;
let ambLight = null
let scene = null
let camera = null
let controls = null
let controls2 = null
let composer = null
let outlinePass = null
let warnPass = null
let serverTips = null;//服务器信息弹窗


function reloadOption(attribute) {
  ambLight.color = new THREE.Color(attribute.ambientLightColor);
}
function clickHandler() {
  const handler = (event) => {
    event.preventDefault();
    const mouthX = (event.offsetX / props.width) * 2 - 1;
    const mouthY = -(event.offsetY / props.height) * 2 + 1;
    // 先汲取屏幕上点击的位置创建一个向量
    let vector3 = new THREE.Vector3(mouthX, mouthY, 0.5);
    // 然后用unproject函数将点击位置发射光线
    vector3 = vector3.unproject(camera);
    // 用Raycaster 对象向点击位置发射光线
    const rayCaster = new THREE.Raycaster(camera.position, vector3.sub(camera.position).normalize());
    const mouse = new THREE.Vector2();
    mouse.x = mouthX;
    mouse.y = mouthY;
    rayCaster.setFromCamera(mouse, camera);

    const intersects = rayCaster.intersectObjects(scene.children, true);
    if (intersects.length < 1) return;
    const currObj = intersects[0].object; //currObj为点击到的第一个对象
    cameraFocus(currObj)//摄像机聚焦机柜
    ejectServer(currObj)//弹出服务器
  }
  renderer.domElement.addEventListener('click', handler.bind(), false);
}
function ejectServer(currObj) {
  if (currObj.name !== 'server') return;
  currObj.userData.ejected = !currObj.userData.ejected
  const { x, y, z } = currObj.position;
  // const offsetZ = currObj.userData.ejected ? 20 : -20;
  // new TWEEN.Tween(currObj.position)
  //     .to({z: currObj.position.z + offsetZ}, 500)
  //     .easing(TWEEN.Easing.Elastic.Out).start();
  if (currObj.userData.ejected) {
    serverTips.position.set(x + 70, y, z + 50)
    serverTips.visible = true
  } else {
    serverTips.visible = false
  }
}
function cameraFocus(currObj) {
  if (currObj.name !== 'cabinet') return;
  const pipeSpline = new THREE.CatmullRomCurve3([
    camera.position,
    new THREE.Vector3(
      currObj.position.x,
      currObj.position.y + 60,
      currObj.position.z + 100
    ),
    new THREE.Vector3(
      currObj.position.x,
      currObj.position.y + 60,
      currObj.position.z + 90
    ),
    new THREE.Vector3(
      currObj.position.x,
      currObj.position.y + 60,
      currObj.position.z + 80
    ),
  ]);
  const geometry = new THREE.BufferGeometry().setFromPoints(
    pipeSpline.getPoints(400)
  );
  // 材质对象
  const material = new THREE.LineBasicMaterial({ color: 'red' });
  // 线条模型对象
  const cameraLine = new THREE.Line(geometry, material);

  let progress = 0;
  const step = 0.0028;
  function animate() {
    if (cameraLine && progress <= 1 - step * 300) {
      const point = pipeSpline.getPointAt(progress); //获取样条曲线指定点坐标，作为相机的位置
      const pointBox = pipeSpline.getPointAt(progress + step * 300); //获取样条曲线指定点坐标
      camera.position.set(point.x, point.y, point.z);
      camera.lookAt(pointBox.x, pointBox.y, pointBox.z);
      // controls.position0.set(point.x, point.y, point.z) //非必要，场景有控件时才加上
      controls.target.set(pointBox.x, pointBox.y, pointBox.z); //非必要，场景有控件时才加上
      progress += step;
    }
    requestAnimationFrame(animate);
  }
  animate();
  // scene.add(cameraLine)
}
function setAlert() {
  let cabMode = scene.getObjectByName('cabinet');
  warnPass.selectedObjects = [cabMode];
}
function addCabinet(attribute) {
  const cabinetModel = createCabinet(60, 200, 60, attribute.cabinetColor); //创建机柜
  const serverModel = createServer(50, 4, 50); //创建服务器
  for (let i = 0; i < 8; i++) {//复制机柜
    for (let j = 0; j < 12; j++) {
      let offsetX = 200;
      if (i % 2 === 0) {
        offsetX = -700
      }
      const [x1, y1, z1] = [62 * j + offsetX, 0, 280 * (Math.floor(i / 2)) - 360];
      const cabinet = cabinetModel.clone();
      cabinet.position.set(x1, y1, z1);
      scene.add(cabinet);
      for (let k = 1; k < 6; k++) {
        const server = serverModel.clone();
        server.position.set(x1, k * 16, z1);
        scene.add(server);
      }
    }
  }
  serverTips = createTips('#server-tips');
  scene.add(serverTips);
}
function createRoom() {
  const floor = createFloor(2000, 1400);
  scene.add(floor);
}
function initSceneCameraRenderer() {
  const dom = document.getElementById(uuid);
  if (!dom) {
    return true
  }
  renderer = createRenderer(props.width, props.height);
  dom.appendChild(renderer.domElement);
  labelRenderer = createLabelRenderer(props.width, props.height);//渲染HTML
  dom.appendChild(labelRenderer.domElement);

  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(45, props.width / props.height, 0.01, 20000);
  camera.position.set(500, 800, 1200);
  controls = orbitCtrl(camera, renderer.domElement)
  controls2 = trackballCtrl(camera, renderer.domElement)
}
function addLight() {
  ambLight = ambientLight
  scene.add(ambLight);
  scene.add(dirLight);
}
function initComposer() {
  composer = new EffectComposer(renderer);

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  outlinePass = new OutlinePass(new THREE.Vector2(props.width, props.height), scene, camera);
  outlinePass.hiddenEdgeColor.set('#1499ff');//高亮颜色
  outlinePass.visibleEdgeColor.set('#1499ff');//模型被挡住时高亮颜色
  composer.addPass(outlinePass);

  warnPass = new OutlinePass(new THREE.Vector2(props.width, props.height), scene, camera);
  warnPass.hiddenEdgeColor.set('#ff0000');
  warnPass.visibleEdgeColor.set('#ff0000');
  composer.addPass(warnPass);

  const FXAAShaderPass = new ShaderPass(FXAAShader);//开启抗锯齿
  FXAAShaderPass.uniforms['resolution'].value.set(1 / props.width, 1 / props.height);
  FXAAShaderPass.renderToScreen = true;
  composer.addPass(FXAAShaderPass);

  composer.render();
}
function addRayCaster() {
  const rayCaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  if (!outlinePass) return;
  const onPointerMove = function (event) {
    if (event.isPrimary === false) return;
    mouse.x = (event.offsetX / props.width) * 2 - 1;
    mouse.y = -(event.offsetY / props.height) * 2 + 1;
    checkIntersection();
  };
  renderer.domElement.addEventListener('pointermove', onPointerMove);
  const checkIntersection = function () {
    rayCaster.setFromCamera(mouse, camera);
    const intersects = rayCaster.intersectObject(scene, true);
    if (intersects.length > 0) {
      const selectedObject = intersects[0].object;
      if (selectedObject.name === "cabinet") {
        outlinePass.selectedObjects = [selectedObject];
      }
    } else {
      outlinePass.selectedObjects = [];
    }
  };
}
function animate() {
  controls.update();
  const target = controls.target;
  controls2.target.set(target.x, target.y, target.z);
  controls2.update();
  if (composer) {
    composer.render();
  }
  // TWEEN.update();
  labelRenderer.render(scene, camera);//渲染html DOM
  requestAnimationFrame(animate);
}


watch(props.option.attribute, (obj) => {
  reloadOption(obj);
})
watch(() => [props.width, props.height], () => {
  renderer.setSize(props.width, props.height)
})
onMounted(() => {
  const isNullElement = initSceneCameraRenderer();
  if (isNullElement) {
    return
  }
  addLight();
  createRoom();//创建机房(地板,围墙)
  addCabinet(props.option.attribute);//创建机柜/服务器
  initComposer();
  addRayCaster();//机柜高亮效果
  animate();
  clickHandler()
  setAlert();//设置告警机柜
})

onBeforeUnmount(() => {
  cancelAnimationFrame(animate);
  if(scene) scene.clear();
  if(renderer) renderer.clear();
  if(controls) controls.dispose();
})
</script>

<style scoped></style>
